import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { teamsAPI, contactsAPI } from '../api';
import { buildInviteUrl } from '../utils/inviteCode';

function InviteUserDialog({ open, onClose, currentUser }) {
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteUrl, setInviteUrl] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [generatedUsername, setGeneratedUsername] = useState('');
  const [baseUsername, setBaseUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [inviteMode, setInviteMode] = useState('team');

  const hasTeams = currentUser?.teams && currentUser.teams.length > 0;

  const resetGenerated = () => {
    setInviteCode(null);
    setInviteUrl('');
    setGeneratedCode(null);
    setGeneratedUsername('');
    setCopied(false);
  };

  useEffect(() => {
    if (open && currentUser) {
      if (hasTeams) {
        setSelectedTeamId(currentUser.teams[0].id);
        setInviteMode('team');
      } else {
        setInviteMode('contact');
        setSelectedTeamId(null);
      }
    } else {
      resetGenerated();
      setBaseUsername('');
      setError(null);
      setSelectedTeamId(null);
      setInviteMode('team');
    }
  }, [open, currentUser, hasTeams]);

  const generateInvite = async () => {
    if ((inviteMode === 'team' || inviteMode === 'loginCode') && !selectedTeamId) {
      setError('Please select a team');
      return;
    }
    if (inviteMode === 'loginCode' && !baseUsername.trim()) {
      setError('Please enter a username');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (inviteMode === 'loginCode') {
        const response = await teamsAPI.inviteUserWithCode(selectedTeamId, baseUsername.trim());
        setGeneratedCode(response.data.code);
        setGeneratedUsername(response.data.username);
        setInviteUrl('');
        setInviteCode(null);
        return;
      }

      const response = inviteMode === 'team'
        ? await teamsAPI.generateInvite(selectedTeamId)
        : await contactsAPI.generateInvite();
      const { inviteCode: code, inviterUsername, inviteDigits } = response.data;
      setInviteCode(code);
      setGeneratedCode(null);
      setGeneratedUsername('');

      const basePath = window.location.pathname;
      const url = buildInviteUrl(basePath, inviterUsername, inviteDigits);
      setInviteUrl(`${window.location.origin}${url}`);
    } catch (generateError) {
      console.error('Failed to generate invite:', generateError);
      setError(generateError.response?.data?.error || 'Failed to generate invite');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    const text = inviteUrl || `${generatedUsername}\nCode: ${generatedCode}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (copyError) {
      console.error('Failed to copy:', copyError);
      const textField = document.getElementById('invite-url-field');
      if (textField) {
        textField.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const inviteDescription = inviteMode === 'team'
    ? 'Share this link with users you want to invite to your team:'
    : inviteMode === 'contact'
      ? 'Share this link to add someone as a direct contact (no team required):'
      : 'Share this username and code with the user you want to invite:';

  const hasResult = Boolean(inviteUrl || generatedCode);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent sx={{ pt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Typography>Generating invite...</Typography>
        ) : hasResult ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {inviteDescription}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                id="invite-url-field"
                fullWidth
                value={inviteUrl || generatedUsername}
                InputProps={{
                  readOnly: true,
                }}
                variant="outlined"
              />
              <IconButton
                color={copied ? 'success' : 'primary'}
                onClick={handleCopy}
                aria-label="copy invite details"
              >
                <ContentCopyIcon />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" color="success.main">
                Copied to clipboard!
              </Typography>
            )}
            {generatedCode && (
              <Alert severity="info">
                Login code: <strong>{generatedCode}</strong>
              </Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ToggleButtonGroup
              value={inviteMode}
              exclusive
              onChange={(_, value) => {
                if (!value) return;
                setInviteMode(value);
                resetGenerated();
                setError(null);
              }}
              fullWidth
              size="small"
            >
              <ToggleButton value="team" disabled={!hasTeams}>
                Invite to team
              </ToggleButton>
              <ToggleButton value="contact">
                Direct contact
              </ToggleButton>
              <ToggleButton value="loginCode" disabled={!hasTeams}>
                Login code
              </ToggleButton>
            </ToggleButtonGroup>

            {inviteMode === 'team' && hasTeams && (
              <>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="team-select-label">Select Team</InputLabel>
                  <Select
                    labelId="team-select-label"
                    value={selectedTeamId || ''}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    label="Select Team"
                  >
                    {currentUser.teams.map(team => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={generateInvite}
                  disabled={loading || !selectedTeamId}
                >
                  Generate Invite Link
                </Button>
              </>
            )}

            {inviteMode === 'contact' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  The invited person can register and chat with you directly, without joining a team.
                </Typography>
                <Button
                  variant="contained"
                  onClick={generateInvite}
                  disabled={loading}
                >
                  Generate Invite Link
                </Button>
              </>
            )}

            {inviteMode === 'loginCode' && hasTeams && (
              <>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="login-code-team-select-label">Select Team</InputLabel>
                  <Select
                    labelId="login-code-team-select-label"
                    value={selectedTeamId || ''}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    label="Select Team"
                  >
                    {currentUser.teams.map(team => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Username"
                  value={baseUsername}
                  onChange={(e) => {
                    setBaseUsername(e.target.value);
                    setError(null);
                  }}
                  helperText="Will be created as: <username>_<6-digit code>"
                />
                <Button
                  variant="contained"
                  onClick={generateInvite}
                  disabled={loading || !selectedTeamId || !baseUsername.trim()}
                >
                  Generate Invite Code
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {hasResult && (
          <Button onClick={generateInvite} variant="outlined" disabled={loading}>
            {inviteUrl ? 'Generate New Link' : 'Generate New Code'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default InviteUserDialog;
